import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../app/store';
import { openShare, planWith, ui } from '../app/ui';
import * as act from '../app/actions';
import { plural } from '../app/words';
import type { Model } from '../data/model';
import { findAddress, inTurn, type Found } from '../data/find';
import { stubbed } from '../data/maps';
import { isShortLink, pinLabel, readPin } from '../data/pins';
import { GOOGLE_MS, deadlineOn, lastDay, problemOf, sheetStopId } from '../data/waiting';
import { stopsBucket, track } from '../data/analytics';
import { readSheet, separatorOf, SheetFileError, columnsOf, blank, type SheetProblem, type SheetTable } from '../sheet/file';
import { canRead, columnsAt, FIELD_LABELS, FIELDS, firstRow, layoutOf, matchColumns, sample, settled, withField, withNotes, type SheetColumns, type SheetField } from '../sheet/columns';
import { collectWords, readMoney, readPhone, readRows, TEMPLATE_URL, type SheetOrder } from '../sheet/reader';
import { EmptyHelp } from '../ui/bits';
import { Icon } from '../ui/Icon';

/**
 * Import a sheet (the phone's Sheet, brought to the web): drop or choose a CSV, TSV or .xlsx, say which row has the column
 * names or that the first row is a stop, match the columns (remembered in this browser for the next sheet laid out the same way), then check every row:
 * what couldn't be read is flagged with its row and why, and fixed in place. Addresses are found as the phone finds
 * them, a customer's pin with no lookup, and the same address is looked up once. Then into Waiting, or straight to Plan
 * a route.
 */

const LAYOUTS = 'routr-sheet-layouts';
const remembered = (): Record<string, string[]> => {
  try {
    return JSON.parse(localStorage.getItem(LAYOUTS) ?? '{}');
  } catch {
    return {};
  }
};
function remember(c: SheetColumns) {
  try {
    localStorage.setItem(LAYOUTS, JSON.stringify({ ...remembered(), [layoutOf(c)]: c.fields.map((f) => f ?? 'asNote') }));
  } catch {
    // A private window can refuse: the columns are asked again next time.
  }
}

const PROBLEMS: Record<SheetProblem, [string, string]> = {
  oldExcel: ['That’s an old Excel file (.xls).', 'In Excel, choose File, Save As, and pick Excel Workbook (.xlsx) or CSV; then choose that file here.'],
  notSpreadsheet: ['That file isn’t a spreadsheet Routr can read.', 'Choose a CSV, a TSV or an Excel file (.xlsx). From Google Sheets or Numbers, download or export it as one of those first.'],
  empty: ['That sheet is empty.', 'Check it’s the right file, and that the stops are on its first sheet.'],
  tooBig: ['That file is too big.', 'A day’s stops fit in far less than 10 MB: take out pictures or other sheets, or save just the stops as a CSV.'],
  oldBrowser: ['This browser is too old to open Excel files.', 'Update it (Safari 16.4, Firefox 113 or Chrome 80 and later can), or save the sheet as a CSV and choose that.'],
};

/** Addresses looked up this visit, by country and address: kept outside the page, so reading the sheet again (or another
 * sheet with the same addresses) asks Google nothing twice. An answer that failed isn't kept, so it's asked again later. */
const looked = new Map<string, Promise<Found>>();

interface Row {
  order: SheetOrder;
  /** Where it is, once found: the customer's pin, or Google's. */
  at: { lat: number; lng: number } | null;
  pin: boolean;
  found: Found | null;
  looking: boolean;
}

export function ImportSheet({ m }: { m: Model }) {
  const [file, setFile] = useState<{ name: string; table: SheetTable } | null>(null);
  const [problem, setProblem] = useState<SheetProblem | null>(null);
  const [columns, setColumns] = useState<SheetColumns | null>(null);
  const [step, setStep] = useState<'file' | 'columns' | 'check'>('file');
  const [drag, setDrag] = useState(false);
  const close = () => ui.set({ importing: false });

  const open = async (f: File) => {
    setProblem(null);
    try {
      const table = await readSheet(new Uint8Array(await f.arrayBuffer()), separatorOf(f.name));
      const c = matchColumns(table, remembered());
      setFile({ name: f.name, table });
      setColumns(c);
      setStep(settled(c) ? 'check' : 'columns');
      track('sheet_opened', { kind: /\.xlsx$/i.test(f.name) ? 'xlsx' : separatorOf(f.name) ? 'tsv' : 'csv', asked: !settled(c), remembered: !!c.remembered, rows: stopsBucket(table.rows.length) });
    } catch (e) {
      const p = e instanceof SheetFileError ? e.problem : 'notSpreadsheet';
      setProblem(p);
      track('sheet_failed', { reason: p });
    }
  };

  return (
    <div className="card import">
      <div className="tools">
        <Icon name="table" />
        <b>Import a sheet</b>
        {file && <span className="cap ellipsis">{file.name}{file.table.sheetName ? ` · ${file.table.sheetName}` : ''}</span>}
        <span className="grow" />
        {step !== 'file' && (
          <button className="btn sm" onClick={() => setStep(step === 'check' ? 'columns' : 'file')}>
            <Icon name="arrowLeft" size="sm" />
            {step === 'check' ? 'The columns' : 'Another file'}
          </button>
        )}
        <button className="iconbtn sm" data-tip="Close" aria-label="Close the import" onClick={close}>
          <Icon name="x" />
        </button>
      </div>
      {step === 'file' && (
        <div
          className={`dropzone${drag ? ' on' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files[0];
            if (f) void open(f);
          }}
        >
          <Icon name="uploadSimple" size="lg" />
          <b>Drop a CSV, TSV or Excel file here</b>
          <span className="cap">One row a stop: an address or a customer’s pin on each. Columns are matched by their names.</span>
          <label className="btn primary">
            Choose a file
            <input type="file" hidden accept=".csv,.tsv,.tab,.xlsx,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => e.target.files?.[0] && void open(e.target.files[0])} />
          </label>
          <a className="link" href={TEMPLATE_URL} target="_blank" rel="noopener">
            <Icon name="downloadSimple" size="sm" />
            Get the template
          </a>
          {problem && (
            <div className="gate-warn" role="alert" style={{ margin: '8px 0 0', maxWidth: 520 }}>
              {PROBLEMS[problem][0]} {PROBLEMS[problem][1]}
            </div>
          )}
        </div>
      )}
      {step === 'columns' && file && columns && <Columns table={file.table} columns={columns} set={setColumns} next={() => (remember(columns), setStep('check'))} />}
      {step === 'check' && file && columns && <Check m={m} file={file} columns={columns} back={() => setStep('columns')} again={() => setStep('file')} />}
    </div>
  );
}

function Columns({ table, columns, set, next }: { table: SheetTable; columns: SheetColumns; set: (c: SheetColumns) => void; next: () => void }) {
  const tops = table.rows
    .map((r, i) => ({ r, i }))
    .filter((x) => !blank(x.r))
    .slice(0, 6);
  const rows = table.rows.slice(columns.headerRow == null ? 0 : columns.headerRow + 1).filter((r) => !blank(r)).length;
  return (
    <div className="rec-b">
      <div className="title">Match the columns.</div>
      <div className="cap" style={{ marginTop: 4 }}>
        {canRead(columns) ? 'A column you don’t match goes to the stop’s notes, so nothing is lost; Leave out drops it.' : 'Which column has the address? Or the customer’s pin, or latitude and longitude.'}
      </div>
      <label className="row" style={{ marginTop: 12 }}>
        <input
          type="checkbox"
          checked={columns.headerRow == null}
          onChange={(e) => {
            set(columnsAt(table, e.target.checked ? null : firstRow(table)));
            track('sheet_first_row_a_stop', { on: e.target.checked });
          }}
        />
        <span>The first row is a stop</span>
        <span className="cap">The sheet has no column names, so you match each column here</span>
      </label>
      {columns.headerRow != null && (
        <label className="row" style={{ marginTop: 8 }}>
          <span className="cap">Column names</span>
          <select className="dateinput" value={columns.headerRow} onChange={(e) => set(columnsAt(table, Number(e.target.value)))} aria-label="Which row has the column names">
            {tops.map(({ r, i }) => (
              <option key={i} value={i}>
                Row {r.number}: {r.cells.filter(Boolean).slice(0, 4).join(', ').slice(0, 60)}
              </option>
            ))}
          </select>
        </label>
      )}
      <table style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>In the sheet</th>
            <th>For example</th>
            <th>Is the</th>
          </tr>
        </thead>
        <tbody>
          {columns.headers.map((h, c) => (
            <tr key={c}>
              <td className="nw">
                <b>{h}</b>
              </td>
              <td className="cap ellipsis" style={{ maxWidth: 360 }}>
                {sample(columns, table, c) || '–'}
              </td>
              <td>
                <select className="dateinput" value={columns.fields[c] ?? ''} onChange={(e) => set(withField(columns, c, e.target.value as SheetField))} aria-label={`“${h}” is the`}>
                  {columns.fields[c] == null && <option value="">Choose…</option>}
                  {FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {FIELD_LABELS[f]}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row" style={{ marginTop: 14 }}>
        <button className="btn primary" disabled={!canRead(columns) || !columnsOf(table)} onClick={next}>
          Read {plural(rows, 'row')}
        </button>
        {columns.remembered && <span className="cap">Matched as last time</span>}
      </div>
    </div>
  );
}

/** One lookup per address, however many rows have it. */
const keyOf = (address: string) => address.trim().toLowerCase().replace(/\s+/g, ' ');

function Check({ m, file, columns, back, again }: { m: Model; file: { name: string; table: SheetTable }; columns: SheetColumns; back: () => void; again: () => void }) {
  const s = useStore(ui);
  const country = m.business?.country ?? null;
  const read = useMemo(() => readRows(file.table, withNotes(columns), { now: m.now, zone: m.zone, currency: m.currency, country, near: m.shop }), [file, columns]);
  const [rows, setRows] = useState<Row[]>([]);
  const [done, setDone] = useState(0);
  const [day, setDay] = useState('');
  const lookups = useRef(0);
  /** Once the account's lookups for the day are used up (maps.ts LIMITS), the rest aren't asked: they'd fail too. */
  const spent = useRef(false);
  const find = (address: string) => {
    const k = `${country ?? ''}|${keyOf(address)}`;
    let p = looked.get(k);
    if (!p) {
      lookups.current++;
      p = findAddress(address, { country, near: m.shop }).then((f) => {
        if (f.error) looked.delete(k);
        if (f.why === 'limit') spent.current = true;
        return f;
      });
      looked.set(k, p);
    }
    return p;
  };
  const place = async (o: SheetOrder): Promise<Row> => {
    const p = o.pin ? readPin(o.pin, m.shop) : null;
    if (p) return { order: o, at: { lat: p.lat, lng: p.lng }, pin: true, found: null, looking: false };
    // A pin the browser can't place (a short Maps link, a short plus code with no shop to read it by), and no address.
    if (o.pin && o.address === o.pin) {
      const error = isShortLink(o.pin) ? 'A short Maps link only opens in a browser: open it, then paste the full link or the coordinates here.' : 'A short plus code needs where routes start: paste the full code or the coordinates.';
      return { order: o, at: null, pin: false, found: { place: null, rough: false, via: null, error }, looking: false };
    }
    if (spent.current && !looked.has(`${country ?? ''}|${keyOf(o.address)}`)) {
      return { order: o, at: null, pin: false, found: { place: null, rough: false, via: null, why: 'limit', error: 'Not looked up: today’s lookups for this account are used up. In Waiting it’s looked up again when it’s planned.' }, looking: false };
    }
    const f = await find(o.address);
    return { order: o, at: f.place ? { lat: f.place.lat, lng: f.place.lng } : null, pin: false, found: f, looking: false };
  };
  useEffect(() => {
    let live = true;
    // Leaving (another file, The columns, closing) stops the lookups not yet started.
    const stop = new AbortController();
    setRows(read.orders.map((o) => ({ order: o, at: null, pin: false, found: null, looking: true })));
    setDone(0);
    void inTurn(read.orders, place, (n) => live && setDone(n), 8, stop.signal).then((placed) => live && setRows(placed));
    return () => {
      live = false;
      stop.abort();
    };
  }, [read]);
  const set = (i: number, next: Partial<Row>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...next } : r)));
  const edit = (i: number, o: Partial<SheetOrder>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, order: { ...r.order, ...o } } : r)));
  const readdress = async (i: number, address: string) => {
    const r = rows[i];
    if (!address.trim() || address === r.order.address) return;
    set(i, { looking: true, order: { ...r.order, address, pin: null, houseNumber: /^(?:plot|no\.?|house|flat|block)?\s*(\d+[a-z]?)\b/i.exec(address)?.[1] ?? null } });
    const next = await place({ ...r.order, address, pin: readPin(address, m.shop) ? address : null });
    setRows((rs) => rs.map((x, k) => (k === i ? next : x)));
  };
  const rephone = (i: number, phone: string) => {
    const o = rows[i].order;
    const flags = o.flags.filter((f) => !/phone number/.test(f));
    const notes = o.notes.filter((n) => !n.startsWith('Phone: '));
    const read = phone.trim() ? readPhone(phone.trim(), notes, flags, country) : null;
    edit(i, { phone: read, flags, notes });
  };
  const recollect = (i: number, text: string) => {
    const o = rows[i].order;
    const flags = o.flags.filter((f) => !/isn’t an amount|isn’t a delivery fee/.test(f));
    const notes = o.notes.filter((n) => !/^(Amount|Delivery fee): /.test(n));
    if (!text.trim()) return edit(i, { collect: null, flags, notes });
    const a = readMoney(text, m.currency, 'order');
    if (!a) return edit(i, { flags: [...flags, `“${text}” isn’t an amount, so nothing to collect is set.`], notes });
    edit(i, { collect: { order: a.order, fee: a.fee ?? 0 }, flags, notes });
  };
  const looking = rows.filter((r) => r.looking).length;
  /** What the rules would refuse (a 501-character address): said on its row, and not written. */
  const refused = (r: Row) => problemOf({ address: r.found?.place?.address || r.order.address }, day || null);
  const ready = rows.filter((r) => !refused(r));
  const placed = ready.filter((r) => r.at);
  const look = (r: Row) => r.order.flags.length > 0 || (!r.looking && (!r.at || r.found?.rough === true)) || (r.order.deliverBy != null && !r.order.deliverBy.today);
  const flagged = rows.filter(look).length;
  const payments = m.business?.payments.on !== false;
  const money = columns.fields.some((f) => f === 'amount' || f === 'total' || f === 'fee');

  /** A row as a stop, stored as the phone stores it; the position only for a plan (a waiting stop keeps Google's apart). */
  const stopOf = (r: Row, withSpot: boolean, ids: Map<SheetOrder, string>) => {
    const o = r.order;
    const id = ids.get(o)!;
    const p = r.found?.place;
    const onlyPin = r.pin && o.address === o.pin;
    // A route today takes its deadline today; a waiting stop keeps the time (byTime) and gets its day when it goes out.
    const by = withSpot && o.deliverBy?.today ? deadlineOn(m.today, o.deliverBy.time) : null;
    const notes = [...o.notes, ...(o.deliverBy && !o.deliverBy.today && !day ? [`Deliver by: ${o.deliverBy.asWritten}`] : []), ...(o.collect && !payments ? [collectWords(o.collect, m.currency)] : [])];
    // A pickup and a delivery with one order number go together: the delivery names its pickup, which goes first.
    const pickup = o.type === 'delivery' && o.pairKey ? [...ids.keys()].filter((x) => x.type === 'pickup' && x.pairKey === o.pairKey && x.row < o.row).at(-1) : undefined;
    return act.storedStop({
      id,
      address: onlyPin ? pinLabel(readPin(o.pin!, m.shop)!) : p?.address || o.address,
      writtenAddress: onlyPin ? null : o.address,
      placeId: p?.placeId ?? null,
      houseNumber: o.houseNumber ?? p?.houseNumber ?? null,
      lat: (withSpot || r.pin) && r.at ? r.at.lat : (null as unknown as number),
      lng: (withSpot || r.pin) && r.at ? r.at.lng : (null as unknown as number),
      precision: r.found?.rough ? 'low' : 'exact',
      type: o.type,
      pickupId: pickup ? ids.get(pickup)! : null,
      recipientName: o.name,
      recipientPhone: o.phone,
      accessInstructions: o.access,
      notes: notes.join(' · ') || null,
      packageCount: o.packages ?? 1,
      size: o.size,
      deliverBy: by,
      ...(o.collect && payments ? { collectOrder: o.collect.order, collectFee: o.collect.fee, currency: m.currency } : {}),
    });
  };
  // The same row of the same sheet always gets the same id: read again, nothing already waiting is added twice.
  const idsFor = (rs: Row[]) => new Map(rs.map((r) => [r.order, sheetStopId(file.name, r.order.row, [r.order.address, r.order.name, r.order.phone].join('|'))]));
  const asWaiting = (rs: Row[]) => {
    const ids = idsFor(rs);
    const at = Date.now();
    return rs.map((r) => {
      const stop = stopOf(r, false, ids);
      const byTime = r.order.deliverBy && (r.order.deliverBy.today || day) ? r.order.deliverBy.time : null;
      return { stop: { ...stop, id: String(stop.id), address: String(stop.address) }, source: 'sheet' as const, sheet: { file: file.name, row: r.order.row }, day: day || null, byTime, google: !r.pin && r.at ? { ...r.at, at } : null };
    });
  };
  const toWaiting = () => {
    act.addWaiting(asWaiting(ready), `${plural(ready.length, 'stop')} from ${file.name} waiting for a route${day ? ` · ${act.dayName(m, day)}` : ''}`);
    track('sheet_added', { to: 'waiting', stops: stopsBucket(ready.length), flagged, not_found: ready.length - placed.length, lookups: lookups.current });
    ui.set({ importing: false, tab: 'wait', wsrc: `sheet:${file.name}`, wday: null, q: '', sel: new Set() });
  };
  /** Into Waiting, then shared out across the couriers on Live (those not on the map are listed there as left out). */
  const toShare = () => {
    const list = asWaiting(ready);
    act.addWaiting(list, `${plural(ready.length, 'stop')} from ${file.name} waiting for a route${day ? ` · ${act.dayName(m, day)}` : ''}`);
    track('sheet_added', { to: 'share', stops: stopsBucket(ready.length), flagged, not_found: ready.length - placed.length, lookups: lookups.current });
    ui.set({ importing: false, tab: 'wait', wsrc: `sheet:${file.name}`, wday: null, q: '', sel: new Set() });
    openShare(list.map((x) => x.stop.id), m.people.map((p) => p.uid));
  };
  const toPlan = () => {
    const ids = idsFor(placed);
    const at = Date.now();
    planWith(
      placed.map((r) => {
        const stop = stopOf(r, true, ids);
        return {
          id: String(stop.id),
          address: String(stop.address),
          writtenAddress: (stop.writtenAddress as string | null) ?? undefined,
          area: String(stop.address).split(',')[1]?.trim() ?? '',
          lat: r.at!.lat,
          lng: r.at!.lng,
          name: r.order.name ?? undefined,
          phone: r.order.phone ?? undefined,
          pin: r.pin,
          googleUntil: r.pin ? null : at + GOOGLE_MS,
          from: stop,
        };
      }),
    );
    // The rows not on the map aren't lost: they wait, to be fixed and looked up again when planned.
    const rest = ready.filter((r) => !r.at);
    if (rest.length) act.addWaiting(asWaiting(rest), `${plural(rest.length, 'stop')} not on the map ${rest.length === 1 ? 'waits' : 'wait'} in Waiting: fix ${rest.length === 1 ? 'its address' : 'their addresses'} there`);
    track('sheet_added', { to: 'plan', stops: stopsBucket(placed.length), flagged, not_found: rest.length, lookups: lookups.current });
    ui.set({ importing: false });
  };

  if (!read.orders.length)
    return (
      <EmptyHelp
        title="Nothing in this sheet could be read as a stop."
        tries={[
          { icon: 'table', label: 'Match the columns again', note: canRead(columns) ? 'Check the row with the column names, and which column has the address' : 'Say which column has the address, or the customer’s pin', act: 'columns' },
          ...read.notOrders.slice(0, 2).map((w) => ({ icon: 'warningCircle', label: w, act: 'columns' })),
          { icon: 'downloadSimple', label: 'Get the template', note: 'One row a stop, with the columns Routr knows', act: 'template' },
          { icon: 'uploadSimple', label: 'Choose another file', act: 'file' },
        ]}
        onTry={(a) => (a === 'template' ? window.open(TEMPLATE_URL, '_blank', 'noopener') : a === 'file' ? again() : back())}
      />
    );
  return (
    <>
      <div className="row" style={{ padding: '12px 16px 0', gap: 16 }}>
        <span className="title">{plural(rows.length, 'row')} found.</span>
        <span className="cap">
          {looking ? `Finding addresses · ${done} of ${rows.length}` : `${placed.length} on the map${rows.length > placed.length ? ` · ${rows.length - placed.length} not found` : ''}`}
          {flagged ? ` · ${flagged} to look at` : ''}
          {stubbed ? ' · emulators: a fixed list of Abuja places answers' : ''}
        </span>
        {looking > 0 && (
          <span className="bar grow" style={{ maxWidth: 200 }}>
            <i style={{ width: `${(done / Math.max(1, rows.length)) * 100}%`, background: 'var(--ink)' }} />
          </span>
        )}
      </div>
      <div className="tw" style={{ marginTop: 8 }}>
        <table className="checklist">
          <thead>
            <tr>
              <th>Row</th>
              <th>Address</th>
              <th>Customer</th>
              <th>Phone</th>
              {money && <th>To collect</th>}
              <th>To look at</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const o = r.order;
              const why = [
                ...(refused(r) ? [`Not added: ${refused(r)}.`] : []),
                ...o.flags,
                ...(r.looking ? [] : r.pin ? [] : !r.at ? [r.found?.error ?? 'Not found on the map: fix the address, or it waits and is looked up again when planned.'] : r.found?.rough ? ['Found only roughly: the courier may need to call.'] : []),
                ...(o.deliverBy && !o.deliverBy.today ? [`Deliver by “${o.deliverBy.asWritten}” is another day: set the day below, or it stays a note.`] : []),
              ];
              return (
                <tr key={`${o.row}-${i}`} className={why.length ? 'look' : ''}>
                  <td className="mono nw">{o.row}</td>
                  <td>
                    <input className="cellin" defaultValue={o.address} onBlur={(e) => void readdress(i, e.target.value.trim())} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} aria-label={`Row ${o.row}: address`} autoComplete="off" autoCorrect="off" spellCheck={false} />
                    <div className="area">{r.looking ? 'Looking it up…' : r.pin ? 'The customer’s pin · no lookup' : r.found?.place ? r.found.place.address : ' '}</div>
                  </td>
                  <td>
                    <input className="cellin" defaultValue={o.name ?? ''} onBlur={(e) => edit(i, { name: e.target.value.trim() || null })} aria-label={`Row ${o.row}: customer`} autoComplete="off" autoCorrect="off" spellCheck={false} />
                  </td>
                  <td>
                    <input className="cellin" defaultValue={o.phone ?? o.notes.find((n) => n.startsWith('Phone: '))?.slice(7) ?? ''} onBlur={(e) => rephone(i, e.target.value)} inputMode="tel" aria-label={`Row ${o.row}: phone`} autoComplete="off" />
                  </td>
                  {money && (
                    <td>
                      <input className="cellin" style={{ width: 110 }} defaultValue={o.collect ? String((o.collect.order + o.collect.fee) / 100) : (o.notes.find((n) => n.startsWith('Amount: '))?.slice(8) ?? '')} onBlur={(e) => recollect(i, e.target.value)} aria-label={`Row ${o.row}: to collect`} autoComplete="off" />
                    </td>
                  )}
                  <td className="cap" style={{ minWidth: 220 }}>
                    {why.length ? why.map((w) => <div key={w}>{w}</div>) : <span className="pos">Ready</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {read.notOrders.length > 0 && (
        <div className="rec-b" style={{ paddingBottom: 0 }}>
          <div className="eyebrow">Not added</div>
          {read.notOrders.map((w) => (
            <div key={w} className="cap" style={{ marginTop: 4 }}>
              {w}
            </div>
          ))}
        </div>
      )}
      <div className="tools importbar">
        <label className="row" style={{ gap: 8 }}>
          <span className="cap">For</span>
          <input type="date" className="dateinput" value={day} min={m.today} max={lastDay(m.today)} onChange={(e) => setDay(e.target.value)} aria-label="The day these stops are for (optional)" />
          {!day && <span className="cap">any day</span>}
        </label>
        <span className="grow" />
        <button className="btn" disabled={looking > 0} onClick={toWaiting}>
          <Icon name="stack" />
          Add {plural(ready.length, 'stop')} to Waiting
        </button>
        <button className="btn" disabled={looking > 0 || !placed.length || !m.people.length} onClick={toShare} data-tip={m.people.length ? 'Added to Waiting, then shared out across your couriers on Live' : 'Invite a courier first'}>
          <Icon name="arrowsSplit" />
          Share out across couriers
        </button>
        <button className="btn primary" disabled={looking > 0 || !placed.length} onClick={toPlan} data-tip={ready.length > placed.length ? `${plural(ready.length - placed.length, 'stop')} not on the map go to Waiting instead` : undefined}>
          <Icon name="path" />
          Plan a route with {plural(placed.length, 'stop')}
        </button>
      </div>
      {s.plan?.stops.length ? <div className="cap" style={{ padding: '0 16px 12px', textAlign: 'right' }}>They join the route being planned on Live.</div> : null}
    </>
  );
}
